// // src/components/Auth/ProtectedRoute.js
// import React from 'react';
// import { Navigate, useLocation } from 'react-router-dom';
// import { useAuth } from '../../contexts/AuthContext';
// import { CircularProgress, Box } from '@mui/material';

// /**
//  * ProtectedRoute component that handles authentication-based routing
//  * It wraps protected content and redirects unauthorized users to the login page
//  * while preserving their intended destination for after login
//  */
// export const ProtectedRoute = ({ children }) => {
//     // Get authentication state and location for redirect handling
//     const { user, loading } = useAuth();
//     const location = useLocation();

//     // Show loading indicator while checking authentication status
//     if (loading) {
//         return (
//             <Box 
//                 sx={{ 
//                     minHeight: '100vh',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     bgcolor: 'background.default'
//                 }}
//             >
//                 <CircularProgress />
//             </Box>
//         );
//     }

//     // If user is not authenticated, redirect to login page
//     // Store the current location so we can redirect back after login
//     if (!user) {
//         return <Navigate to="/login" state={{ from: location }} replace />;
//     }

//     // If user is authenticated, render the protected content
//     return children;
// };

// /**
//  * Higher-order component that wraps routes requiring authentication
//  * This allows us to protect routes in a more declarative way
//  */
// export const withAuth = (Component) => {
//     return function AuthenticatedComponent(props) {
//         return (
//             <ProtectedRoute>
//                 <Component {...props} />
//             </ProtectedRoute>
//         );
//     };
// };

// // Example usage of the higher-order component:
// // export const ProtectedDashboard = withAuth(Dashboard);

// export default ProtectedRoute;

// src/components/Auth/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * ProtectedRoute component that handles authentication-based routing
 * It wraps protected content and redirects unauthorized users to the login page
 * while preserving their intended destination for after login
 */
export const ProtectedRoute = ({ children, requireAdmin = false }) => {
    // Get authentication state and location for redirect handling
    const { user, loading } = useAuth();
    const location = useLocation();
    const { t } = useTranslation();

    // Show loading indicator while checking authentication status
    if (loading) {
        return (
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // Debug logging
    console.log('ProtectedRoute - User:', user);
    console.log('ProtectedRoute - requireAdmin:', requireAdmin);
    console.log('ProtectedRoute - user.isAdmin:', user?.isAdmin);

    // If user is not authenticated, redirect to landing page
    if (!user) {
        console.log('No user found, redirecting to home');
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // If admin access is required but user is not an admin
    if (requireAdmin && !user.isAdmin) {
        console.log('Admin access required but user is not admin');
        console.log('User object:', JSON.stringify(user, null, 2));
        
        return (
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    p: 3
                }}
            >
                <Alert severity="error" sx={{ maxWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('admin.navigation.accessDenied')}
                    </Typography>
                    <Typography variant="body2">
                        You do not have sufficient privileges to access this area.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    // If user is authenticated (and admin if required), render the protected content
    return children;
};

/**
 * Higher-order component that wraps routes requiring authentication
 * This allows us to protect routes in a more declarative way
 */
export const withAuth = (Component) => {
    return function AuthenticatedComponent(props) {
        return (
            <ProtectedRoute>
                <Component {...props} />
            </ProtectedRoute>
        );
    };
};

// Example usage of the higher-order component:
// export const ProtectedDashboard = withAuth(Dashboard);

export default ProtectedRoute;